import { FLUID_CONTRAST, FLUID_RAMP_MID, FLUID_WARP } from "./constants";
import { fbm } from "./noise";
import type { OrbPalette } from "./palette";

export type FluidParams = {
  /** Integrated flow clock, in arbitrary units. Never `t * speed`. */
  time: number;
  /** Exposure, 0..1 — how far up the colour ramp the field reaches. */
  bright: number;
  /** Domain-warp strength, 0..1. Higher churns more. */
  flow: number;
  /**
   * Shaping exponent on the field. 2 keeps mid-tones dark so light reads as
   * thin wisps; below 1 expands the bright end, pushing more of the field past
   * the glow stop and into near-white. Lower means more white.
   */
  gamma: number;
  palette: OrbPalette;
};

export type Fluid = {
  /** The offscreen buffer, ready to be drawn into the orb's clip. */
  readonly canvas: HTMLCanvasElement;
  paint(params: FluidParams): void;
};

/**
 * The orb's animated interior: a domain-warped fBm field painted per-pixel into
 * a small offscreen buffer, then upscaled into the orb's circular clip.
 *
 * It flows but never rotates — there is no angular term anywhere in here.
 *
 * Colour comes entirely from the orb's own tokens, ramped
 * `fluid-low -> glow -> ring`, so the fluid can only ever produce on-palette
 * teal. `fluid-low` defaults to the disc colour and is overridden per state.
 */
export function createFluid(size: number): Fluid | null {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const image = ctx.createImageData(size, size);
  const data = image.data;

  function paint({ time, bright, flow, gamma, palette }: FluidParams) {
    const { fluidLow, glow, ring } = palette;
    const warp = flow * FLUID_WARP;
    const exposure = 0.35 + bright * 0.9;

    for (let y = 0; y < size; y += 1) {
      const v = (y / size) * 2 - 1;
      for (let x = 0; x < size; x += 1) {
        const i = (y * size + x) * 4;
        const u = (x / size) * 2 - 1;
        const distSq = u * u + v * v;

        if (distSq > 1) {
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 0;
          continue;
        }
        const dist = Math.sqrt(distSq);

        // Domain warp: offset the sample point by another noise field. This is
        // what turns bland blobs into something that reads as fluid.
        const wx =
          u * 1.2 + fbm(u * 1.3 + time * 0.85, v * 1.3 - time * 0.55) * warp;
        const wy =
          v * 1.2 + fbm(u * 1.2 - time * 0.6, v * 1.4 + time * 0.7) * warp;

        const n1 = fbm(wx + time * 0.45, wy - time * 0.35);
        const n2 = fbm(wx * 1.6 - time * 0.3, wy * 1.6 + 2.4);

        // Stretch contrast about the field's ~0.5 mean, then shape it. `gamma`
        // decides how much of the field reads as light: 2 crushes mid-tones so
        // only peaks show, below 1 opens the bright end up into white.
        let e = (n1 * 0.65 + n2 * 0.35 - 0.26) * FLUID_CONTRAST;
        e = e < 0 ? 0 : e > 1 ? 1 : e;
        e = e ** gamma;
        // Spherical falloff: brighter toward the middle, dark at the rim.
        e *= exposure * (0.5 + (1 - dist) * 0.6);
        e = e < 0 ? 0 : e > 1 ? 1 : e;

        // Ramp low -> glow -> ring, written channel-wise to avoid allocating.
        let r: number;
        let g: number;
        let b: number;
        if (e < FLUID_RAMP_MID) {
          const k = e / FLUID_RAMP_MID;
          r = fluidLow.r + (glow.r - fluidLow.r) * k;
          g = fluidLow.g + (glow.g - fluidLow.g) * k;
          b = fluidLow.b + (glow.b - fluidLow.b) * k;
        } else {
          const k = (e - FLUID_RAMP_MID) / (1 - FLUID_RAMP_MID);
          r = glow.r + (ring.r - glow.r) * k;
          g = glow.g + (ring.g - glow.g) * k;
          b = glow.b + (ring.b - glow.b) * k;
        }

        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        // Feather the last few percent so the disc has no hard pixel edge;
        // the renderer's clip handles the rest.
        data[i + 3] = dist > 0.94 ? (1 - dist) / 0.06 * 255 : 255;
      }
    }

    ctx!.putImageData(image, 0, 0);
  }

  return { canvas, paint };
}
